import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { euros, fecha, fechaHora } from "../lib/formato";
import { Aviso, Campo, Cargando, Etiqueta, Panel } from "../componentes/piezas";

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rol: string;
  activo: boolean;
  ultimoAcceso: string | null;
}

interface Pago {
  id: string;
  importeCentimos: number;
  periodoHasta: string;
  metodo: string;
  notas: string | null;
  fecha: string;
  registradoPor: string | null;
}

/**
 * Lo que hace falta cuando un club llama.
 *
 * Antes, un "no podemos entrar" un sábado no tenía respuesta desde aquí: había
 * que entrar en la base de datos a mano. Ahora se ve quién tiene cuenta y se le
 * puede mandar su enlace de recuperación.
 *
 * Sigue sin verse un solo dato de socios, que es la promesa que el club compró.
 */
export function EquipoDelClub({ clubId }: { clubId: string }) {
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState("");

  const datos = useQuery({
    queryKey: ["plataforma-equipo", clubId],
    queryFn: () => api<{ usuarios: Usuario[] }>(`/plataforma/clubes/${clubId}/equipo`),
  });

  async function mandarEnlace(u: Usuario) {
    setError("");
    setMensaje("");
    setEnviando(u.id);
    try {
      await api(`/plataforma/clubes/${clubId}/equipo/${u.id}/recuperar`, { metodo: "POST" });
      setMensaje(`Enlace enviado a ${u.email}. Caduca en una hora.`);
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo enviar");
    } finally {
      setEnviando("");
    }
  }

  return (
    <Panel titulo="Equipo del club" sinCuerpo>
      <div style={{ padding: "0 14px" }}>
        <Aviso tipo="error">{error}</Aviso>
        <Aviso tipo="ok">{mensaje}</Aviso>
      </div>

      {datos.isLoading || !datos.data ? (
        <Cargando />
      ) : (
        <div className="tabla-envoltorio">
          <table className="tabla">
            <tbody>
              {datos.data.usuarios.map((u) => (
                <tr key={u.id} style={u.activo ? undefined : { opacity: 0.55 }}>
                  <td>
                    <strong>{u.nombre}</strong>
                    <div className="suave pequeno">{u.email}</div>
                  </td>
                  <td className="pequeno">
                    <Etiqueta tipo="gris">{u.rol}</Etiqueta>
                    {!u.activo && <div className="suave">desactivado</div>}
                  </td>
                  <td className="pequeno suave">
                    {u.ultimoAcceso ? `entró ${fechaHora(u.ultimoAcceso)}` : "nunca ha entrado"}
                  </td>
                  <td>
                    {u.activo && (
                      <button
                        className="boton fantasma"
                        disabled={enviando === u.id}
                        onClick={() => void mandarEnlace(u)}
                      >
                        {enviando === u.id ? "Enviando..." : "Mandarle acceso"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="suave pequeno" style={{ padding: "10px 14px", margin: 0 }}>
        No puedes ver ni cambiar la contraseña de nadie: «Mandarle acceso» le envía a esa persona el mismo enlace que
        pediría desde la pantalla de entrada.
      </p>
    </Panel>
  );
}

/** Cobros del club: hasta cuándo está pagado y qué se le ha cobrado. */
export function CobrosDelClub({
  clubId,
  cuotaMensualCentimos,
  pagadoHasta,
  alCobrar,
}: {
  clubId: string;
  cuotaMensualCentimos: number;
  pagadoHasta: string | null;
  alCobrar: () => void;
}) {
  const cliente = useQueryClient();
  const [datos, setDatos] = useState({ importeEuros: "", meses: "1", metodo: "TRANSFERENCIA", notas: "" });
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const pagos = useQuery({
    queryKey: ["plataforma-pagos", clubId],
    queryFn: () => api<Pago[]>(`/plataforma/clubes/${clubId}/pagos`),
  });

  const alCorriente = pagadoHasta != null && new Date(pagadoHasta) > new Date();

  async function apuntar() {
    setError("");
    setEnviando(true);
    try {
      await api(`/plataforma/clubes/${clubId}/pagos`, {
        metodo: "POST",
        cuerpo: {
          importeEuros: Number(datos.importeEuros || cuotaMensualCentimos / 100),
          meses: Number(datos.meses || 1),
          metodo: datos.metodo,
          notas: datos.notas || null,
        },
      });
      setDatos({ importeEuros: "", meses: "1", metodo: "TRANSFERENCIA", notas: "" });
      void cliente.invalidateQueries({ queryKey: ["plataforma-pagos", clubId] });
      alCobrar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : "No se pudo apuntar el cobro");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Panel titulo="Cobros">
      <Aviso tipo="error">{error}</Aviso>

      <p className={alCorriente ? "verde" : "suave"} style={{ marginTop: 0 }}>
        {pagadoHasta ? (
          <>
            {alCorriente ? "Pagado hasta el" : "Venció el"} <strong>{fecha(pagadoHasta)}</strong>
          </>
        ) : (
          "Todavía no se le ha cobrado nada."
        )}
      </p>

      <div className="rejilla dos">
        <Campo etiqueta="Importe (EUR)" ayuda={`Vacío = su cuota, ${euros(cuotaMensualCentimos)}.`}>
          <input
            type="number"
            step="0.01"
            min="0"
            value={datos.importeEuros}
            onChange={(e) => setDatos((d) => ({ ...d, importeEuros: e.target.value }))}
          />
        </Campo>
        <Campo etiqueta="Meses que cubre">
          <input
            type="number"
            min="1"
            max="36"
            value={datos.meses}
            onChange={(e) => setDatos((d) => ({ ...d, meses: e.target.value }))}
          />
        </Campo>
      </div>

      <Campo etiqueta="Cómo pagó">
        <select value={datos.metodo} onChange={(e) => setDatos((d) => ({ ...d, metodo: e.target.value }))}>
          <option value="TRANSFERENCIA">Transferencia</option>
          <option value="DOMICILIACION">Domiciliación</option>
          <option value="EFECTIVO">Efectivo</option>
          <option value="TARJETA">Tarjeta</option>
          <option value="OTRO">Otro</option>
        </select>
      </Campo>

      <button className="boton" disabled={enviando} onClick={() => void apuntar()} style={{ marginBottom: 12 }}>
        {enviando ? "Guardando..." : "Apuntar cobro"}
      </button>

      {(pagos.data ?? []).length > 0 && (
        <div className="tabla-envoltorio">
          <table className="tabla">
            <tbody>
              {(pagos.data ?? []).map((p) => (
                <tr key={p.id}>
                  <td className="pequeno suave">{fecha(p.fecha)}</td>
                  <td className="pequeno">
                    {p.metodo.toLowerCase()}
                    <div className="suave">cubre hasta {fecha(p.periodoHasta)}</div>
                  </td>
                  <td className="num tabular">{euros(p.importeCentimos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
